import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, Typography, makeStyles } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import dayjs from "dayjs";
import * as React from "react";
import { useHistory } from "react-router";
import { useRecoilCallback, useRecoilValue, useRecoilValueLoadable } from "recoil";

import { LeaseContract } from "@/api/generated";
import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { buildThisMonthCashflow } from "@/logics/thisMonthCashflow";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import theme from "@/theme";
import { getLatestMonthlyCashflowResult, getMonthlyCashflowResultEffectiveOn } from "@/utilities/propertyEdit";
import { PropertyEditPageState } from "@/view_models/atoms";
import { MonthlyCashflowResultsOnToday } from "@/view_models/balancesheetSelectors";
import { SelectedProperty } from "@/view_models/propertySelectors";

import CashflowInfoIcon from "@assets/image/ds_v2_cashflow_info.svg";

import { CustomButton } from "../Button";
import { Loading } from "../Loading";

import { AccordionDetailItemRow } from "./AccordionDetailItemRow";
import { CashflowAverage } from "./CashflowAverage";
import { CashflowRoundingNote } from "./CashflowRoundingNote";
import { DetailRow, DetailSection, useDetailStyles } from "./DetailSection";

import type { ManagementDisplayMode } from "./ManagementDisplayControl";

const useStyles = makeStyles(() => ({
  root: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr)",
    minWidth: 0,
    gap: layout.space[8],
    paddingBottom: layout.space[40],
    [theme.breakpoints.up("md")]: { gap: layout.space[24] },
  },
  cashflow: { padding: layout.space[24], [theme.breakpoints.up("md")]: { padding: 0 } },
  heading: { ...textStyles["SP/Heading/H2"], marginBottom: layout.space[8] },
  description: { ...textStyles["SP/Body/Small"], color: colors["text/secondary"], marginBottom: layout.space[16] },
  answer: {
    background: colors["surface/card"],
    borderRadius: layout.radius.lg,
    padding: layout.space[24],
    marginBottom: layout.space[24],
  },
  answerLabel: { ...textStyles["SP/Body/Small"], color: colors["text/secondary"], marginBottom: layout.space[8] },
  answerNumber: { ...textStyles["Number/L"] },
  breakdown: {
    display: "grid",
    gap: layout.space[24],
    [theme.breakpoints.up("md")]: { gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)" },
  },
  breakdownHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: layout.space[12],
  },
  number: { ...textStyles["Number/M"] },
  bar: {
    display: "flex",
    height: 8,
    borderRadius: layout.radius.full,
    overflow: "hidden",
    marginBottom: layout.space[16],
  },
  item: {
    display: "flex",
    justifyContent: "space-between",
    gap: layout.space[8],
    padding: "4px 0",
    ...textStyles["SP/Body/Small"],
  },
  legend: { display: "flex", alignItems: "center", gap: layout.space[8], minWidth: 0, color: colors["text/secondary"] },
  dot: { width: 8, height: 8, borderRadius: layout.radius.full, flexShrink: 0 },
  navigation: { [theme.breakpoints.up("md")]: { display: "none" } },
  expanded: {
    boxShadow: "none",
    background: colors["surface/card"],
    "&.Mui-expanded": { margin: 0 },
    "&::before": { display: "none" },
    [theme.breakpoints.up("md")]: { borderRadius: layout.radius.lg, overflow: "hidden" },
  },
  accordionSummaryRow: { minHeight: 56, padding: "0 24px" },
  detail: { width: "100%", padding: "0 24px 24px" },
}));

export enum ExpandItem {
  ManagementCost = "management_cost",
  LoanInfo = "loan_info",
  LeaseContract = "lease_contract",
  InsuranceInfo = "insurance_info",
}
type expandItem = ExpandItem | "";

export const ManagementDetailCashflow: React.FC<{
  navigation?: React.ReactNode;
  displayMode?: ManagementDisplayMode;
}> = ({ navigation, displayMode: requestedDisplayMode }) => {
  const classes = useStyles();
  const displayMode = requestedDisplayMode ?? "detail";
  const { isMobile } = useDevice();
  const detailClasses = useDetailStyles();
  const history = useHistory();
  const [expanded, setExpanded] = React.useState<expandItem>("");
  const [leaseContractThisMonth, setLeaseContractThisMonth] = React.useState<LeaseContract>(null);
  const [monthlyCashflowResultSumByItem, setMonthlyCashflowResultSumByItem] = React.useState({
    property_tax_sum: 0,
    large_scale_repair_fund_cost_sum: 0,
    misc_onetime_cost_sum: 0,
  });
  const property = useRecoilValue(SelectedProperty);
  const propertyId = property.id;
  const isBuilding = property.property.property_category === "一棟物件";

  const monthly_cashflow_results_loadable = useRecoilValueLoadable(MonthlyCashflowResultsOnToday);

  // useRecoilCallbackを使用して非同期の状態更新を行う
  const updatePropertyEditPageState = useRecoilCallback(({ snapshot, set }) => async (newState) => {
    set(PropertyEditPageState, newState);
    // 状態が更新されるのを待つ
    await snapshot.getPromise(PropertyEditPageState);
  });

  React.useEffect(() => {
    // 物件切り替え時に前物件の状態が残らないように初期化
    setLeaseContractThisMonth(null);

    if (property.lease_contracts?.length) {
      const leaseContract = property.lease_contracts.find(
        (leaseContract) =>
          (dayjs(leaseContract.starts_at).isBefore(dayjs(), "month") ||
            dayjs(leaseContract.starts_at).isSame(dayjs(), "month")) &&
          (dayjs(leaseContract.ends_at).isAfter(dayjs(), "month") ||
            dayjs(leaseContract.ends_at).isSame(dayjs(), "month"))
      );
      if (leaseContract) {
        setLeaseContractThisMonth(leaseContract);
      }
    }

    // 単発でかかる経費の項目ごとの合計取得（物件切替時のレスポンス順の逆転をガード）
    let cancelled = false;
    PropallyApiFactory()
      .v1PropertiesPropertyIdMonthlyCashflowResultSumByItemGet(propertyId, { withCredentials: true })
      .then((res) => {
        if (!cancelled) setMonthlyCashflowResultSumByItem(res.data);
      })
      .catch(() => {
        // noop
      });

    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  if (monthly_cashflow_results_loadable.state === "loading") {
    return <Loading />;
  }
  if (monthly_cashflow_results_loadable.state === "hasError") {
    throw monthly_cashflow_results_loadable.contents;
  }

  const monthly_cashflow_results = monthly_cashflow_results_loadable.contents;

  // 今月の収支カードの値は月次計算結果から組み立てる（購入月は購入日からの日割り、実績更新の入力はその月以降に反映）。
  // 累積収支・収支シミュレーションと同じ値になるよう、ここで入力値や契約情報から再計算しない。
  const thisMonth = buildThisMonthCashflow(property.property.property_category, monthly_cashflow_results.result);
  const { income, expense, totalCashFlow } = thisMonth;

  // 経費情報・借入情報のアコーディオンは、実績更新で入力した「今月の時点で有効な」月額をそのまま見せる
  // （購入月でも日割りにしない。未来の月に入力した値は今月には出さない）。
  // 月額経費が未設定なら0円。日割り済みのカード金額にはフォールバックしない。
  const effectiveUserInput = (key: string): number | undefined => {
    const effective = getMonthlyCashflowResultEffectiveOn(
      property.user_input_monthly_cashflow_results ?? [],
      key,
      dayjs()
    );
    return (effective as unknown as Record<string, number | undefined>)?.[key];
  };
  const buildingManagementCostInput = effectiveUserInput("building_management_cost") ?? 0;
  const rentalManagementCostInput = effectiveUserInput("management_cost") ?? 0;
  const repairFundCostInput = effectiveUserInput("repair_fund_cost") ?? 0;
  const communicationCostInput = effectiveUserInput("communication_cost") ?? 0;
  const miscCostInput = effectiveUserInput("misc_cost") ?? 0;
  const loanInterestRatePercentageThisMonth =
    effectiveUserInput("loan_interest_rate_percentage") ??
    monthly_cashflow_results.result.loan_interest_rate_percentage ??
    0;

  const suppliesCostSum =
    property.user_input_monthly_cashflow_results?.reduce((sum, r) => sum + (r.supplies_cost || 0), 0) ?? 0;

  let incomeItems: Array<[string, string]>;
  let expenseItems: Array<[string, string]>;
  if (property.property.property_category === "一棟物件") {
    // 一棟物件の場合の構成
    incomeItems = [
      ["月間家賃収入", `${Math.round(thisMonth.rentIncome).toLocaleString()}円`],
      ["その他月間収入（駐車場など）", `${Math.round(thisMonth.otherIncome).toLocaleString()}円`],
    ];
    expenseItems = [
      ["ローン返済（利息含む）", `${Math.round(thisMonth.loanRepayment).toLocaleString()}円`],
      [
        "管理会社委託費（建物＋賃貸管理含む）",
        `${Math.round(thisMonth.buildingManagementCompanyCommission).toLocaleString()}円`,
      ],
      ["共用部水道光熱費", `${Math.round(thisMonth.buildingSharedUtilityExpenses).toLocaleString()}円`],
      ["その他経費", `${Math.round(thisMonth.buildingMiscCost).toLocaleString()}円`],
    ];
  } else {
    // 区分マンションの場合の構成
    incomeItems = [
      ["家賃収入", `${Math.round(thisMonth.rentIncome).toLocaleString()}円`],
      ["管理費収入", `${Math.round(thisMonth.managementFeeIncomeOnly).toLocaleString()}円`],
      ["その他収入", `${Math.round(thisMonth.otherIncome).toLocaleString()}円`],
    ];
    expenseItems = [
      ["ローン返済（利息含む）", `${Math.round(thisMonth.loanRepayment).toLocaleString()}円`],
      ["建物管理費", `${Math.round(thisMonth.buildingManagementCost).toLocaleString()}円`],
      ["賃貸管理費", `${Math.round(thisMonth.rentalManagementCost).toLocaleString()}円`],
      ["修繕積立金", `${Math.round(thisMonth.repairFundCost).toLocaleString()}円`],
      ["通信費", `${Math.round(thisMonth.communicationCost).toLocaleString()}円`],
      ["その他経費", `${Math.round(thisMonth.miscCost).toLocaleString()}円`],
    ];
  }

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // 賃貸借契約開始時における収入
  const typeOfIncomeBeginningContractMap = {
    KeyMoney: "礼金",
    SecurityDeposit: "更新料",
  };
  const typeOfIncomeBeginningContract = leaseContractThisMonth?.type_of_the_income_of_the_beginning_of_contract
    ? typeOfIncomeBeginningContractMap[leaseContractThisMonth?.type_of_the_income_of_the_beginning_of_contract]
    : "礼金";
  let leaseContractIncome = "-ヶ月分";
  if (leaseContractThisMonth?.type_of_the_income_of_the_beginning_of_contract === "KeyMoney") {
    leaseContractIncome = `${leaseContractThisMonth?.key_money.toLocaleString()}ヶ月分`;
  } else if (leaseContractThisMonth?.type_of_the_income_of_the_beginning_of_contract === "SecurityDeposit") {
    leaseContractIncome = `${leaseContractThisMonth?.security_deposit.toLocaleString()}ヶ月分`;
  }

  // 賃貸借契約開始時における支出
  const outsourcingFeeNew = leaseContractThisMonth?.outsourcing_fee_rate_of_new_lease_contract
    ? `${leaseContractThisMonth.outsourcing_fee_rate_of_new_lease_contract.toLocaleString()}ヶ月分`
    : "-";
  const outsourcingFeeUpdating = leaseContractThisMonth?.outsourcing_fee_rate_of_updating_lease_contract
    ? `${leaseContractThisMonth.outsourcing_fee_rate_of_updating_lease_contract.toLocaleString()}ヶ月分`
    : "-";

  // 保険情報
  const fireInsuranceResult = getLatestMonthlyCashflowResult(
    property.user_input_monthly_cashflow_results,
    "fire_insurance_cost"
  );
  const fireInsuranceStartMonth = fireInsuranceResult?.month
    ? dayjs(fireInsuranceResult.month).format("YYYY/MM/DD")
    : "";

  const earthquakeInsuranceResult = getLatestMonthlyCashflowResult(
    property.user_input_monthly_cashflow_results,
    "earthquake_insurance_cost"
  );
  const earthquakeInsuranceStartMonth = earthquakeInsuranceResult?.month
    ? dayjs(earthquakeInsuranceResult.month).format("YYYY/MM/DD")
    : "";

  const renderBreakdown = (title: string, total: number, items: Array<[string, string]>, palette: string[]) => {
    const amounts = items.map(([, value]) => Math.max(0, Number(value.replace(/[^0-9.-]/g, "")) || 0));
    const sum = amounts.reduce((acc, value) => acc + value, 0);
    return (
      <Box>
        <Box className={classes.breakdownHeader}>
          <Typography>{title}</Typography>
          <Typography className={classes.number}>
            {total.toLocaleString()}
            <Box component="span" fontSize={12}>
              円
            </Box>
          </Typography>
        </Box>
        <Box className={classes.bar}>
          {amounts.map((amount, index) => (
            <Box
              key={items[index][0]}
              style={{ width: `${sum ? (amount / sum) * 100 : 0}%`, background: palette[index % palette.length] }}
            />
          ))}
        </Box>
        {items.map(([label, value], index) => (
          <Box key={label} className={classes.item} data-cashflow-item={label}>
            <Box className={classes.legend}>
              <Box className={classes.dot} style={{ background: palette[index % palette.length] }} />
              {label}
            </Box>
            <Box>{value}</Box>
          </Box>
        ))}
      </Box>
    );
  };
  return (
    <Box
      className={classes.root}
      id="management-cashflow-content"
      role={requestedDisplayMode ? "tabpanel" : undefined}
      aria-labelledby={requestedDisplayMode ? `management-display-${displayMode}` : undefined}
    >
      <Box className={classes.cashflow} aria-label="当月の収支">
        <Typography component="h2" className={classes.heading}>
          収支
        </Typography>
        <Typography className={classes.description}>この物件の収入と支出を確認できます。</Typography>
        <Box className={classes.answer} id="management-tour-cashflow">
          <Typography className={classes.answerLabel}>今月の収支（ローン返済含む）</Typography>
          <Typography
            className={classes.answerNumber}
            style={{ color: totalCashFlow < 0 ? colors["text/error"] : colors["text/primary"] }}
          >
            {totalCashFlow.toLocaleString()}
            <Box component="span" fontSize={14}>
              円
            </Box>
          </Typography>
          <CashflowAverage property={property} />
          {displayMode === "simple" && (
            <Box mt={2} aria-label="収支の合計">
              <DetailRow
                label={isBuilding ? `収入合計（${property.building_room?.length ?? 0}室）` : "収入合計"}
                value={`${income.toLocaleString()}円`}
              />
              <DetailRow
                label={isBuilding ? "運営費合計（建物全体）" : "運営費合計"}
                value={`${Math.round(thisMonth.unroundedExpense - thisMonth.loanRepayment).toLocaleString()}円`}
              />
              <DetailRow label="ローン返済" value={`${Math.round(thisMonth.loanRepayment).toLocaleString()}円`} />
              <Box display="flex" alignItems="flex-start" gridGap={8} mt={1}>
                <img src={CashflowInfoIcon} alt="" style={{ flexShrink: 0, marginTop: 2 }} />
                <Typography className={classes.description} style={{ marginBottom: 0 }}>
                  固定資産税・保険料・一時的な支出は含みません。詳細表示で確認できます。
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        {displayMode === "detail" && (
          <Box className={classes.breakdown}>
            {renderBreakdown("収入", income, incomeItems, [
              colors["chart/income-1"],
              colors["chart/income-2"],
              colors["chart/other"],
            ])}
            {renderBreakdown("支出", expense, expenseItems, [
              colors["chart/expense-1"],
              colors["chart/expense-2"],
              colors["chart/expense-3"],
              colors["chart/expense-4"],
              colors["chart/other"],
            ])}
          </Box>
        )}
        <Box mt={2}>
          <CashflowRoundingNote />
        </Box>
      </Box>
      {isMobile && <Box className={classes.navigation}>{navigation}</Box>}
      {displayMode === "detail" && (
        <>
          {property.property.property_category === "一棟物件" ? (
            <DetailSection
              title="部屋別詳細"
              id="management-tour-rooms"
              editLabel="追加"
              onEdit={async () => {
                await updatePropertyEditPageState("building_room");
                history.push("/edit_room?mode=create");
              }}
            >
              {property.building_room?.map((room) => (
                <Box key={room.id} mb={2}>
                  <CustomButton
                    customVariant="outlinedGray"
                    fullWidth
                    onClick={async () => {
                      await updatePropertyEditPageState("building_room");
                      history.push(`/edit_room?mode=edit&selectedRoomId=${room.id}`);
                    }}
                  >
                    {room.room_number}号室　編集
                  </CustomButton>
                  <DetailRow
                    label="月間家賃収入"
                    value={room.monthly_fee ? `${Math.round(room.monthly_fee).toLocaleString()}円` : "-円"}
                  />
                  <DetailRow
                    label="その他月間収入（駐車場など）"
                    value={
                      room.other_monthly_income ? `${Math.round(room.other_monthly_income).toLocaleString()}円` : "-円"
                    }
                  />
                  <DetailRow
                    label="賃貸借契約の開始日"
                    value={room.starts_at ? dayjs(room.starts_at).format("YYYY年MM月DD日") : "-"}
                  />
                  <DetailRow
                    label="賃貸借契約の終了日"
                    value={room.ends_at ? dayjs(room.ends_at).format("YYYY年MM月DD日") : "-"}
                  />
                </Box>
              ))}
            </DetailSection>
          ) : (
            <DetailSection
              title="賃貸契約情報"
              onEdit={async () => {
                await updatePropertyEditPageState("lease_contract");
                history.push("/edit_property");
              }}
            >
              <Box className={detailClasses.rows}>
                {/* 賃貸契約情報 */}
                <AccordionDetailItemRow
                  v2
                  title="家賃収入"
                  value={
                    leaseContractThisMonth?.monthly_fee
                      ? `${Math.round(leaseContractThisMonth.monthly_fee).toLocaleString()}円`
                      : "-円"
                  }
                />
                <AccordionDetailItemRow
                  v2
                  title="管理費収入"
                  value={
                    leaseContractThisMonth?.monthly_management_fee
                      ? `${Math.round(leaseContractThisMonth.monthly_management_fee).toLocaleString()}円`
                      : "-円"
                  }
                />
                <AccordionDetailItemRow
                  v2
                  title="その他収入（駐車場など）"
                  value={
                    leaseContractThisMonth?.other_monthly_income
                      ? `${Math.round(leaseContractThisMonth.other_monthly_income).toLocaleString()}円`
                      : "-円"
                  }
                />
                <AccordionDetailItemRow
                  v2
                  title="賃貸借契約の開始日"
                  value={
                    leaseContractThisMonth?.starts_at
                      ? `${dayjs(leaseContractThisMonth.starts_at).format("YYYY年MM月DD日")}`
                      : "-"
                  }
                />
                <AccordionDetailItemRow
                  v2
                  title="賃貸借契約の終了日"
                  value={
                    leaseContractThisMonth?.ends_at
                      ? `${dayjs(leaseContractThisMonth.ends_at).format("YYYY年MM月DD日")}`
                      : "-"
                  }
                />
                {leaseContractThisMonth?.type_of_the_income_of_the_beginning_of_contract && (
                  <AccordionDetailItemRow v2 title={typeOfIncomeBeginningContract} value={leaseContractIncome} />
                )}
                {leaseContractThisMonth?.type_of_the_income_of_the_beginning_of_contract === "KeyMoney" && (
                  <AccordionDetailItemRow v2 title="新規賃貸契約手数料" value={outsourcingFeeNew} />
                )}
                {leaseContractThisMonth?.type_of_the_income_of_the_beginning_of_contract === "SecurityDeposit" && (
                  <AccordionDetailItemRow v2 title="賃貸契約更新時手数料" value={outsourcingFeeUpdating} />
                )}
              </Box>
            </DetailSection>
          )}
          <DetailSection
            title="借入情報"
            onEdit={async () => {
              await updatePropertyEditPageState("loan_info");
              history.push("/edit_property");
            }}
          >
            <Box className={detailClasses.rows}>
              {/* 借入情報 */}
              <AccordionDetailItemRow
                v2
                title="借入金額"
                value={`${Math.round(property.property.loan_amount).toLocaleString()}円`}
              />
              <AccordionDetailItemRow v2 title="借入利率 (年利)" value={`${loanInterestRatePercentageThisMonth}%`} />
              <AccordionDetailItemRow
                v2
                title="初回返済年月"
                value={`${dayjs(property.property.payment_starts_at).format("YYYY年MM月")}`}
              />
              <AccordionDetailItemRow v2 title="返済年数" value={`${property.property.payment_period}年`} />
            </Box>
          </DetailSection>
          <DetailSection
            title="保険情報"
            onEdit={async () => {
              await updatePropertyEditPageState("insurance_info");
              history.push("/edit_property");
            }}
          >
            <Box className={detailClasses.rows}>
              {/* 保険情報 */}
              <AccordionDetailItemRow
                v2
                title="火災保険料"
                value={
                  fireInsuranceResult?.fire_insurance_cost
                    ? `${fireInsuranceResult.fire_insurance_cost.toLocaleString()}円`
                    : "-円"
                }
              />
              <AccordionDetailItemRow
                v2
                title="火災保険期間"
                value={
                  fireInsuranceStartMonth !== "" && fireInsuranceResult?.fire_insurance_holding_period != null
                    ? `${fireInsuranceStartMonth} ~ ${dayjs(fireInsuranceStartMonth)
                        .add(fireInsuranceResult.fire_insurance_holding_period, "year")
                        .format("YYYY/MM/DD")}`
                    : "-"
                }
              />
              <AccordionDetailItemRow
                v2
                title="地震保険料"
                value={
                  earthquakeInsuranceResult?.earthquake_insurance_cost
                    ? `${earthquakeInsuranceResult.earthquake_insurance_cost.toLocaleString()}円`
                    : "-円"
                }
              />
              <AccordionDetailItemRow
                v2
                title="地震保険期間"
                value={
                  earthquakeInsuranceStartMonth !== "" &&
                  earthquakeInsuranceResult?.earthquake_insurance_holding_period != null
                    ? `${earthquakeInsuranceStartMonth} ~ ${dayjs(earthquakeInsuranceStartMonth)
                        .add(earthquakeInsuranceResult.earthquake_insurance_holding_period, "year")
                        .format("YYYY/MM/DD")}`
                    : "-"
                }
              />
            </Box>
          </DetailSection>
          {property.property.property_category === "一棟物件" ? (
            // 一棟物件向け 経費情報
            <Accordion
              expanded={expanded === ExpandItem.ManagementCost}
              onChange={handleAccordionChange(ExpandItem.ManagementCost)}
              classes={{ root: classes.expanded }}
              square
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummaryRow}>
                <Typography variant="h4" color="textPrimary">
                  経費情報
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.detail}>
                <Box className={classes.detail}>
                  <Typography variant="caption" color="textSecondary">
                    今月適用中の月額（日割り前）。未設定の項目は0円です。
                  </Typography>
                  {/* 経費情報 */}
                  <AccordionDetailItemRow
                    v2
                    title="管理会社委託費（建物＋賃貸管理含む）"
                    value={`${Math.round(
                      property.property.building_detail?.management_company_commission || 0
                    ).toLocaleString()}円`}
                  />
                  <AccordionDetailItemRow
                    v2
                    title="共用部水道光熱費"
                    value={`${Math.round(
                      property.property.building_detail?.shared_utility_expenses || 0
                    ).toLocaleString()}円`}
                  />
                  <AccordionDetailItemRow
                    v2
                    title="その他経費"
                    value={`${Math.round(
                      property.property.building_detail?.misc_building_cost || 0
                    ).toLocaleString()}円`}
                  />
                  <Divider />
                  <Box py={2}>
                    <Typography variant="h4" color="textPrimary">
                      一過性の支出
                    </Typography>
                  </Box>
                  <AccordionDetailItemRow
                    v2
                    title="固定資産税・都市計画税"
                    value={
                      monthlyCashflowResultSumByItem.property_tax_sum
                        ? `${monthlyCashflowResultSumByItem.property_tax_sum.toLocaleString()}円`
                        : "-円"
                    }
                    showFinalTaxReturn={!monthlyCashflowResultSumByItem.property_tax_sum}
                  />
                  <AccordionDetailItemRow
                    v2
                    title="設備修繕、大規模修繕費用"
                    value={
                      monthlyCashflowResultSumByItem.large_scale_repair_fund_cost_sum
                        ? `${monthlyCashflowResultSumByItem.large_scale_repair_fund_cost_sum.toLocaleString()}円`
                        : "-円"
                    }
                    showFinalTaxReturn={!monthlyCashflowResultSumByItem.large_scale_repair_fund_cost_sum}
                  />
                  <AccordionDetailItemRow
                    v2
                    title="消耗品費"
                    value={suppliesCostSum ? `${suppliesCostSum.toLocaleString()}円` : "-円"}
                    showFinalTaxReturn={!suppliesCostSum}
                  />
                  <AccordionDetailItemRow
                    v2
                    title="その他一過性の支出"
                    value={
                      monthlyCashflowResultSumByItem.misc_onetime_cost_sum
                        ? `${monthlyCashflowResultSumByItem.misc_onetime_cost_sum.toLocaleString()}円`
                        : "-円"
                    }
                    showFinalTaxReturn={!monthlyCashflowResultSumByItem.misc_onetime_cost_sum}
                  />

                  <Box mb={2} justifyContent="center">
                    <CustomButton
                      customVariant="outlinedPrimary"
                      fullWidth
                      isEdit
                      onClick={async () => {
                        await updatePropertyEditPageState("management_building_cost");
                        history.push("/edit_property");
                      }}
                    >
                      編集する
                    </CustomButton>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ) : (
            // 区分マンション向け 経費情報
            <Accordion
              expanded={expanded === ExpandItem.ManagementCost}
              onChange={handleAccordionChange(ExpandItem.ManagementCost)}
              classes={{ root: classes.expanded }}
              square
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummaryRow}>
                <Typography variant="h4" color="textPrimary">
                  経費情報
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.detail}>
                <Box className={classes.detail}>
                  <Typography variant="caption" color="textSecondary">
                    今月適用中の月額（日割り前）。未設定の項目は0円です。
                  </Typography>
                  {/* 経費情報 */}
                  <AccordionDetailItemRow
                    v2
                    title="建物管理費"
                    value={`${Math.round(buildingManagementCostInput).toLocaleString()}円`}
                  />
                  <AccordionDetailItemRow
                    v2
                    title="賃貸管理費"
                    value={`${Math.round(rentalManagementCostInput).toLocaleString()}円`}
                  />
                  <AccordionDetailItemRow
                    v2
                    title="修繕積立金"
                    value={`${Math.round(repairFundCostInput).toLocaleString()}円`}
                  />
                  <AccordionDetailItemRow
                    v2
                    title="通信費"
                    value={`${Math.round(communicationCostInput).toLocaleString()}円`}
                  />
                  <AccordionDetailItemRow
                    v2
                    title="その他経費"
                    value={`${Math.round(miscCostInput).toLocaleString()}円`}
                  />
                  <Divider />
                  <Box py={2}>
                    <Typography variant="h4" color="textPrimary">
                      一過性の支出
                    </Typography>
                  </Box>
                  <AccordionDetailItemRow
                    v2
                    title="固定資産税・都市計画税"
                    value={
                      monthlyCashflowResultSumByItem.property_tax_sum
                        ? `${monthlyCashflowResultSumByItem.property_tax_sum.toLocaleString()}円`
                        : "-円"
                    }
                    showFinalTaxReturn={!monthlyCashflowResultSumByItem.property_tax_sum}
                  />
                  <AccordionDetailItemRow
                    v2
                    title="設備修繕、大規模修繕費用"
                    value={
                      monthlyCashflowResultSumByItem.large_scale_repair_fund_cost_sum
                        ? `${monthlyCashflowResultSumByItem.large_scale_repair_fund_cost_sum.toLocaleString()}円`
                        : "-円"
                    }
                    showFinalTaxReturn={!monthlyCashflowResultSumByItem.large_scale_repair_fund_cost_sum}
                  />
                  <AccordionDetailItemRow
                    v2
                    title="消耗品費"
                    value={suppliesCostSum ? `${suppliesCostSum.toLocaleString()}円` : "-円"}
                    showFinalTaxReturn={!suppliesCostSum}
                  />
                  <AccordionDetailItemRow
                    v2
                    title="その他一過性の支出"
                    value={
                      monthlyCashflowResultSumByItem.misc_onetime_cost_sum
                        ? `${monthlyCashflowResultSumByItem.misc_onetime_cost_sum.toLocaleString()}円`
                        : "-円"
                    }
                    showFinalTaxReturn={!monthlyCashflowResultSumByItem.misc_onetime_cost_sum}
                  />

                  <Box mb={2} justifyContent="center">
                    <CustomButton
                      customVariant="outlinedPrimary"
                      fullWidth
                      isEdit
                      onClick={async () => {
                        await updatePropertyEditPageState("management_cost");
                        history.push("/edit_property");
                      }}
                    >
                      編集する
                    </CustomButton>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}
    </Box>
  );
};
